import {
    DeferredFixedPaymentRow,
    DeferredVariablePaymentRow,
    DeferredVariablePaymentType,
    NO_VALUE,
    PutCallRight,
} from './term-form';

export interface RowVisibilityOptions {
    readOnly?: boolean;
    editingRowIndex?: number | null;
}

export function isZeroValue(value: string | number | null | undefined): boolean {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text !== '' && Number(text) === 0;
}

function isBlankValue(value: string | number | null | undefined): boolean {
    return value === null || value === undefined || String(value).trim() === '';
}

function hasPaymentValue(value: string | number | null | undefined): boolean {
    return !isBlankValue(value) && !isZeroValue(value);
}

const DVP_NON_DETAIL_FIELDS: (keyof DeferredVariablePaymentRow)[] = [
    'valueAtClosing',
    'type',
    'considerationForEmployees',
    'buyerConsiderationForEmployees',
    'fairValue',
    'fairValueExplanation',
];

const DVP_FIELD_DEFAULTS: Partial<Record<keyof DeferredVariablePaymentRow, unknown>> = {
    employmentContingency: NO_VALUE,
    putCallRight: PutCallRight.NA,
};

const DFP_NON_DETAIL_FIELDS: (keyof DeferredFixedPaymentRow)[] = [
    'amount',
    'type',
    'considerationForEmployees',
    'buyerConsiderationForEmployees',
    'fairValue',
    'fairValueExplanation',
];

const DFP_FIELD_DEFAULTS: Partial<Record<keyof DeferredFixedPaymentRow, unknown>> = {
    contingent: NO_VALUE,
    employmentContingency: NO_VALUE,
};

function hasDetailData<TRow extends object>(
    row: TRow,
    nonDetailFields: (keyof TRow)[],
    fieldDefaults: Partial<Record<keyof TRow, unknown>>
): boolean {
    return (Object.keys(row) as (keyof TRow)[]).some((field) => {
        if (nonDetailFields.includes(field)) return false;
        const value = row[field] as string | number | null | undefined;
        if (isBlankValue(value)) return false;
        return fieldDefaults[field] !== value;
    });
}

export function hasDvpDetailData(row: DeferredVariablePaymentRow): boolean {
    return hasDetailData(row, DVP_NON_DETAIL_FIELDS, DVP_FIELD_DEFAULTS);
}

export function hasDfpDetailData(row: DeferredFixedPaymentRow): boolean {
    return hasDetailData(row, DFP_NON_DETAIL_FIELDS, DFP_FIELD_DEFAULTS);
}

export function isPhantomDvpRow(row: DeferredVariablePaymentRow): boolean {
    return isZeroValue(row.valueAtClosing) && !hasDvpDetailData(row);
}

export function isPhantomDfpRow(row: DeferredFixedPaymentRow): boolean {
    return isZeroValue(row.amount) && !hasDfpDetailData(row);
}

export function isRecordedDvpRow(row: DeferredVariablePaymentRow): boolean {
    return hasPaymentValue(row.valueAtClosing) || hasDvpDetailData(row);
}

export function isRecordedDfpRow(row: DeferredFixedPaymentRow): boolean {
    return hasPaymentValue(row.amount) || hasDfpDetailData(row);
}

export function visibleDvpRowIndices(
    rows: DeferredVariablePaymentRow[],
    options: RowVisibilityOptions = {}
): number[] {
    return rows
        .map((_, index) => index)
        .filter((index) => {
            if (index === options.editingRowIndex) return true;
            return options.readOnly ? isRecordedDvpRow(rows[index]) : !isPhantomDvpRow(rows[index]);
        });
}

export function visibleDfpRowIndices(
    rows: DeferredFixedPaymentRow[],
    options: RowVisibilityOptions = {}
): number[] {
    return rows
        .map((_, index) => index)
        .filter((index) => {
            if (index === options.editingRowIndex) return true;
            return options.readOnly ? isRecordedDfpRow(rows[index]) : !isPhantomDfpRow(rows[index]);
        });
}

export function dvpPaymentRowsOfType(
    rows: DeferredVariablePaymentRow[],
    type: DeferredVariablePaymentType
): DeferredVariablePaymentRow[] {
    return rows.filter((row) => row.type === type && !isPhantomDvpRow(row));
}

export function dfpPaymentRows(rows: DeferredFixedPaymentRow[]): DeferredFixedPaymentRow[] {
    return rows.filter((row) => !isPhantomDfpRow(row));
}

export function findDvpSummaryRowIndex(
    rows: DeferredVariablePaymentRow[],
    type: DeferredVariablePaymentType
): number {
    const paymentIndex = rows.findIndex((row) => row.type === type && !isPhantomDvpRow(row));
    return paymentIndex >= 0 ? paymentIndex : rows.findIndex((row) => row.type === type);
}

export function findDvpSummaryRow(
    rows: DeferredVariablePaymentRow[],
    type: DeferredVariablePaymentType
): DeferredVariablePaymentRow | undefined {
    const index = findDvpSummaryRowIndex(rows, type);
    return index >= 0 ? rows[index] : undefined;
}

export function findDfpSummaryRowIndex(rows: DeferredFixedPaymentRow[]): number {
    const paymentIndex = rows.findIndex((row) => !isPhantomDfpRow(row));
    if (paymentIndex >= 0) return paymentIndex;
    return rows.length > 0 ? 0 : -1;
}

export function findDfpSummaryRow(
    rows: DeferredFixedPaymentRow[]
): DeferredFixedPaymentRow | undefined {
    const index = findDfpSummaryRowIndex(rows);
    return index >= 0 ? rows[index] : undefined;
}
